// WHERE A LINE'S DISCOUNT PERCENTAGE CAME FROM.
//
// discountResolver.ts decides WHAT the percentage is. This records WHERE it
// came from, so a line can answer "why this number" without re-running the
// resolver against today's rules -- which is exactly the restatement the
// recorded split exists to prevent. Re-deriving provenance later is the same
// bug in a new place: archive the rule and the line would start claiming it
// had no source, having been priced off one.
//
// ⚠️ ADDITIVE. NOTHING HERE CHANGES A NUMBER. The resolution order is
// untouched (manual > pending > rule > refuse), discount_pct is untouched,
// and a line carrying provenance prices byte-identically to one without.
// discountProvenance.test.ts pins that this module names no engine identifier
// and imports nothing from services/pricing.
//
// ⚠️ THE FOUR RESOLVER OUTCOMES COLLAPSE TO THREE SOURCES, and the collapse
// is deliberate:
//
//   rule     -> "rule"    + the rule's id. The only fact here that cannot be
//                          recovered later, which is why it is stored.
//   manual   -> "manual"  + no id. A person stated the cost.
//   refused  -> "none"    The resolver ran and produced nothing.
//   pending  -> "none"    The resolver ran and had no customer to ask about.
//
// Refused and pending both record "none" because the QUESTION this field
// answers -- where did the percentage come from -- has the same answer for
// both: nowhere, there is no percentage. The DIFFERENCE between them is
// already carried, and carried better, by priceStatusReason: a refused line
// has one and a pending line does not. Splitting "none" into two values would
// put that distinction in two places and let them drift.

import type { DiscountSource, Material, OemDiscountRule } from "@/types";
import type { DiscountResolution } from "@/services/discountResolver";

// ⚠️ DiscountSource IS DECLARED IN types/index.ts, not here, and re-exported
// so a reader who finds this module first still gets it from one place. This
// module imports Material; declaring the union here too would close an import
// cycle between the two files.
//
// `undefined` is deliberately NOT one of its members. A line with no recorded
// source predates provenance -- unknown. "none" is a positive claim that the
// resolver ran and found nothing. The backend column is NULL for the
// historical rows and the serializer sends null, never "none".
export type { DiscountSource };

export interface DiscountProvenance {
  source: DiscountSource;
  /** The rule that supplied the percentage. Present ONLY when source is
   *  "rule" -- the model refuses the other combinations outright. */
  ruleId?: string;
}

/** What a freshly resolved line records.
 *
 *  One call site (addMaterialsToQuote), so there is no second place a source
 *  can be invented, exactly as resolveDiscount has one call site for the
 *  percentage itself. */
export function provenanceFromResolution(
  resolution: DiscountResolution,
): DiscountProvenance {
  switch (resolution.kind) {
    case "rule":
      return { source: "rule", ruleId: resolution.ruleId };
    case "manual":
      return { source: "manual" };
    case "refused":
    case "pending":
      return { source: "none" };
  }
}

/** What a line records the moment a person states the cost themselves.
 *
 *  ⚠️ IT DROPS THE RULE ID, and that is a decision rather than an oversight.
 *  A line created off a rule and then overridden by hand was NOT priced off
 *  that rule any more -- the typed figure is what it carries. Keeping the
 *  reference would leave a line whose source says "a person chose this" while
 *  pointing at a rate card row that did not produce it, which is precisely
 *  the "explanation that was never true" this slice exists to prevent. The
 *  model enforces it: a non-"rule" source may not carry a rule id.
 *
 *  ⚠️ TYPING A LIST PRICE ONTO A RULE-SOURCED LINE IS NOT THIS. That edit
 *  supplies the figure the rule's percentage applies TO; the percentage still
 *  came from the rule. Only a typed NET COST or a hand-entered PERCENTAGE
 *  changes where the discount came from. See the two call sites in
 *  MaterialTable. */
export const MANUAL_PROVENANCE: DiscountProvenance = { source: "manual" };

/** "Customer / Brand" for the rule with this id, or "" if it is not in the
 *  list handed over.
 *
 *  ⚠️ RESOLVED FROM THE RULES THAT WERE ACTUALLY MATCHED AGAINST, at the
 *  moment of the match, and then STORED on the line. It is not a live lookup:
 *  a later reopen reads the pairing back off the wire, from the rule the
 *  BACKEND joined, so an archived rule still names itself. Calling this at
 *  render time against the current rule list would blank the label the moment
 *  a rule was archived, which is the one case it exists to explain. */
export function rulePairingLabel(
  rules: readonly OemDiscountRule[],
  ruleId: string,
): string {
  const rule = rules.find((r) => r.id === ruleId);
  if (!rule) return "";
  const customer = rule.partnerName ?? "";
  const brand = rule.brandName ?? "";
  if (!customer && !brand) return "";
  return `${customer || "?"} / ${brand || "?"}`;
}

/** Does this line have a recorded source at all?
 *
 *  ⚠️ THE PREDICATE IS THE ABSENT FIELD, NOT A COMPARISON WITH "none". The
 *  5 live drafts predate provenance and carry nothing; a line that resolved
 *  to "none" carries a real answer. `hasProvenance(line) === false` is
 *  "unknown" and must render as nothing at all -- never as a refusal claim
 *  and never as a confident "manually entered". */
export function hasProvenance(
  line: Pick<Material, "discountSource">,
): boolean {
  return line.discountSource != null;
}

/** The one-line explanation shown beside the discount.
 *
 *  Returns "" for a line with no recorded source, so an unknown renders as
 *  absent rather than as a sentence asserting something.
 *
 *  ⚠️ THE PAIRING, NOT THE PERCENTAGE. A rule-sourced line names the customer
 *  and brand the rate was registered under, because that is what a reader
 *  needs to go and check it. The percentage is already in the cell beside
 *  this text; repeating it here would put the same number in two places and
 *  let a stale one linger. */
export function provenanceLabel(
  line: Pick<Material, "discountSource" | "discountRulePairing">,
): string {
  if (!hasProvenance(line)) return "";
  switch (line.discountSource) {
    case "rule":
      return line.discountRulePairing
        ? `Rate card: ${line.discountRulePairing}`
        : "Rate card rule";
    case "manual":
      return "Manually entered";
    case "none":
      // ⚠️ NO WORDING ABOUT REFUSAL HERE. A refused line already says
      // Unpriced and carries priceStatusReason, which names both remedies.
      // A second sentence would compete with it and, on a PENDING line --
      // same source, no refusal -- would be simply wrong.
      return "";
    default:
      return "";
  }
}
